/**
 * useConversations Hook
 *
 * Handles fetching and managing the conversation list from API.
 * Uses the new /api/v1/conversations endpoint with long polling support.
 */

import {useState, useCallback, useMemo, useEffect, useRef} from 'react'
import {useDebounce} from './useDebounce'
import {ConversationData, SortOrder} from '../types/inbox-v3.types'
import {mapApiConversations} from '../services/inboxMapper'
import {
  useLazyGetConversationsQuery,
  useGetConversationsQuery,
} from '../../../../../services/cellcast'
import type {GetConversationsParams} from '../../../../../services/cellcast.conversations.types'

// ============================================================================
// Types
// ============================================================================

// Message filter types matching inbox-v2
export type MessageFilter =
  | 'All Message'
  | 'Active Messages Only'
  | 'My Message'
  | 'Archived'
  | 'Flagged'
  | 'Unread'
  | 'Opened'
  | 'Subscribed'
  | 'Unsubscribed'
  | null

// Search mode types
export type SearchMode = 'all' | 'by-filter'

// Assignee filter types
export type AssigneeFilter = 'my-chat' | 'unassigned' | string | null

// Filter state interface (matches QuickFiltersModal)
export interface FilterState {
  searchType: SearchMode | null
  assignedTo: AssigneeFilter
  messageFilter: MessageFilter
  sortOrder: SortOrder
}

interface UseConversationsOptions {
  initialFilter?: MessageFilter
  initialSortOrder?: SortOrder
  initialSearchMode?: SearchMode
  searchQuery?: string
  pageSize?: number
  autoFetch?: boolean
  /** Polling interval in ms (0 = disabled, default: 30000) */
  pollingInterval?: number
}

interface UseConversationsReturn {
  // Data
  conversations: ConversationData[]
  totalConversations: number
  openCount: number
  isLoading: boolean
  isFetching: boolean
  error: string | null

  // Pagination
  currentPage: number
  totalPages: number
  hasNextPage: boolean
  hasPrevPage: boolean

  // Filter state
  searchQuery: string
  sortOrder: SortOrder
  messageFilter: MessageFilter
  assigneeFilter: AssigneeFilter
  searchMode: SearchMode
  filterState: FilterState
  activeFilterCount: number

  // Actions
  setSearchQuery: (query: string) => void
  setMessageFilter: (filter: MessageFilter) => void
  setAssigneeFilter: (filter: AssigneeFilter) => void
  setSortOrder: (order: SortOrder) => void
  setSearchMode: (mode: SearchMode) => void
  toggleSortOrder: () => void
  applyFilters: (filters: Partial<FilterState>) => void
  clearFilters: () => void
  goToPage: (page: number) => void
  nextPage: () => void
  prevPage: () => void
  refresh: () => void
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Map MessageFilter to API filter parameter
 * API supports: 'all' | 'unread' | 'flagged' | 'assigned' | 'archived' | 'closed'
 *
 * Note: 'all' typically excludes archived by default.
 * Use includeArchived: true to include archived in 'all' results.
 */
const mapFilterToApiParam = (
  filter: MessageFilter
): {filter: GetConversationsParams['filter']; includeArchived?: boolean} => {
  switch (filter) {
    case 'Archived':
      return {filter: 'archived'}
    case 'Flagged':
      return {filter: 'flagged'}
    case 'Unread':
      return {filter: 'unread'}
    case 'My Message':
      return {filter: 'assigned'} // "My Message" means assigned to current user
    case 'Active Messages Only':
      return {filter: 'all'} // Open/Active = not archived (API default)
    case 'All Message':
      return {filter: 'all', includeArchived: true} // All = include archived
    case 'Subscribed':
      return {filter: 'all'}
    case 'Unsubscribed':
      return {filter: 'blocked'}
    default:
      return {filter: 'all'}
  }
}

/**
 * Map SortOrder to API sortOrder parameter
 */
const mapSortOrderToApi = (order: SortOrder): 'asc' | 'desc' => {
  return order === 'ascend' ? 'asc' : 'desc'
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useConversations(options: UseConversationsOptions = {}): UseConversationsReturn {
  const {
    initialFilter = 'Active Messages Only',
    initialSortOrder = 'descend',
    initialSearchMode = 'by-filter',
    searchQuery: initialSearchQuery = '',
    pageSize = 50,
    autoFetch = true,
    pollingInterval = 30000,
  } = options

  // Local state for filters
  const [messageFilter, setMessageFilter] = useState<MessageFilter>(initialFilter)
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder)
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery)
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilter>(null)
  const [searchMode, setSearchMode] = useState<SearchMode>(initialSearchMode)
  const [currentPage, setCurrentPage] = useState(1)

  // Debounce search query to prevent excessive API calls (400ms delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 400)

  // Track initial fetch completion
  const hasInitialFetchRef = useRef(false)

  // Build query params (uses debounced search to prevent excessive API calls)
  const queryParams = useMemo<GetConversationsParams>(() => {
    const filterResult = mapFilterToApiParam(messageFilter)
    const params: GetConversationsParams = {
      page: currentPage,
      limit: pageSize,
      filter: filterResult.filter,
      sortBy: 'lastMessageAt',
      sortOrder: mapSortOrderToApi(sortOrder),
    }

    // Include archived if the filter specifies it (e.g., "All Message" tab)
    if (filterResult.includeArchived) {
      params.includeArchived = true
    }

    // Add search if present (using debounced value)
    if (debouncedSearchQuery.trim()) {
      params.search = debouncedSearchQuery.trim()
    }

    // If search mode is 'all', only apply search, ignore other filters
    if (searchMode === 'all' && debouncedSearchQuery.trim()) {
      // Reset filter to 'all' when searching across all messages
      params.filter = 'all'
      params.includeArchived = true // Search across everything
      return params
    }

    // Apply assignee filter (overrides messageFilter if set)
    if (assigneeFilter === 'my-chat') {
      params.filter = 'assigned'
      // Note: API should handle "my-chat" to mean current user
    } else if (assigneeFilter === 'unassigned') {
      // For unassigned, we may need to pass a specific parameter
      // For now, keep the current filter but could add assignedTo: null logic
    } else if (assigneeFilter) {
      // Specific team member ID
      params.assignedTo = assigneeFilter
    }

    return params
  }, [
    currentPage,
    pageSize,
    messageFilter,
    sortOrder,
    debouncedSearchQuery,
    searchMode,
    assigneeFilter,
  ])

  // ============================================================================
  // RTK Query - Long Polling for Conversation List Updates
  // ============================================================================

  // Use the query hook with pollingInterval for automatic long polling
  const {
    data: pollingData,
    isLoading: isPollingLoading,
    isFetching: isPollingFetching,
    error: pollingError,
    refetch: refetchPolling,
  } = useGetConversationsQuery(queryParams, {
    skip: !autoFetch,
    pollingInterval: pollingInterval > 0 ? pollingInterval : 0,
    refetchOnMountOrArgChange: true,
  })

  // Lazy query for manual fetches
  const [fetchConversationsManual, {isFetching: isManualFetching}] = useLazyGetConversationsQuery()

  // Combined states
  const isLoading = isPollingLoading
  const isFetching = isPollingFetching || isManualFetching
  const data = pollingData
  const error = pollingError

  // Mark initial fetch as complete when we get data
  useEffect(() => {
    if (data?.data?.conversations && !hasInitialFetchRef.current) {
      hasInitialFetchRef.current = true
    }
  }, [data])

  // Transform API data to inbox-v3 types
  const conversations = useMemo<ConversationData[]>(() => {
    if (!data?.data?.conversations) {
      return []
    }
    return mapApiConversations(data.data.conversations)
  }, [data])

  // Pagination info
  const pagination = useMemo(() => {
    const pag = data?.data?.pagination
    return {
      currentPage: pag?.page || 1,
      totalPages: pag?.totalPages || 1,
      totalDocs: pag?.totalDocs || 0,
      hasNextPage: pag?.hasNextPage ?? false,
      hasPrevPage: pag?.hasPrevPage ?? false,
    }
  }, [data])

  // Calculate open count (unread, non-archived)
  const openCount = useMemo(() => {
    return conversations.filter((c) => !c.contacts.isArchived && !c.contacts.isInboxRead).length
  }, [conversations])

  // Fetch conversations (manual trigger)
  const doFetch = useCallback(() => {
    fetchConversationsManual(queryParams)
  }, [fetchConversationsManual, queryParams])

  // Actions
  const toggleSortOrder = useCallback(() => {
    setSortOrder((prev) => (prev === 'ascend' ? 'descend' : 'ascend'))
  }, [])

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  const nextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      setCurrentPage((prev) => prev + 1)
    }
  }, [pagination.hasNextPage])

  const prevPage = useCallback(() => {
    if (pagination.hasPrevPage) {
      setCurrentPage((prev) => prev - 1)
    }
  }, [pagination.hasPrevPage])

  const refresh = useCallback(() => {
    refetchPolling()
  }, [refetchPolling])

  // Combined filter state (for QuickFiltersModal)
  const filterState = useMemo<FilterState>(
    () => ({
      searchType: searchMode,
      assignedTo: assigneeFilter,
      messageFilter,
      sortOrder,
    }),
    [searchMode, assigneeFilter, messageFilter, sortOrder]
  )

  // Count active filters (for badge display)
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (messageFilter && messageFilter !== 'Active Messages Only') count++
    if (assigneeFilter) count++
    if (searchQuery.trim()) count++
    return count
  }, [messageFilter, assigneeFilter, searchQuery])

  // Apply multiple filters at once (from QuickFiltersModal)
  const applyFilters = useCallback((filters: Partial<FilterState>) => {
    if (filters.searchType !== undefined) {
      setSearchMode(filters.searchType || 'by-filter')
    }
    if (filters.assignedTo !== undefined) {
      setAssigneeFilter(filters.assignedTo)
    }
    if (filters.messageFilter !== undefined) {
      setMessageFilter(filters.messageFilter)
    }
    if (filters.sortOrder !== undefined) {
      setSortOrder(filters.sortOrder)
    }
  }, [])

  // Clear all filters
  const clearFilters = useCallback(() => {
    setMessageFilter('Active Messages Only')
    setAssigneeFilter(null)
    setSearchMode('by-filter')
    setSortOrder('descend')
  }, [])

  // Reset page when filters change (using debounced search to sync with API calls)
  useEffect(() => {
    setCurrentPage(1)
  }, [messageFilter, debouncedSearchQuery, assigneeFilter])

  return {
    // Data
    conversations,
    totalConversations: pagination.totalDocs,
    openCount,
    isLoading,
    isFetching,
    error: error ? (error as any)?.message || 'Failed to fetch conversations' : null,

    // Pagination
    currentPage: pagination.currentPage,
    totalPages: pagination.totalPages,
    hasNextPage: pagination.hasNextPage,
    hasPrevPage: pagination.hasPrevPage,

    // Filter state
    searchQuery,
    sortOrder,
    messageFilter,
    assigneeFilter,
    searchMode,
    filterState,
    activeFilterCount,

    // Actions
    setSearchQuery,
    setMessageFilter,
    setAssigneeFilter,
    setSortOrder,
    setSearchMode,
    toggleSortOrder,
    applyFilters,
    clearFilters,
    goToPage,
    nextPage,
    prevPage,
    refresh,
  }
}